import { Grid, Button } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { USER_ROLES } from "../../helpers/constants";
import { useAccessControl } from "../../hooks/auth/useCheckPermission";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useBanks } from "../../services/bankingService";
import TableService from "../../services/tableService";

import { BANK_TABLE_NAME, useBankTableFieldsConfig } from "./BankTableFieldsConfig";
import { AddBankDialog } from "./AddBankDialog";
import { useDialogBetter } from "../../hooks/useDialogBetter";

import BankingNavigationBar from "./BankingNavigationBar";

export default function FinancialPanelBankPage(props) {
    const { hasAccess } = useAccessControl([USER_ROLES.INVESTMENT_DPI, USER_ROLES.ADMIN, USER_ROLES.ACCOUNT]);

    if (!hasAccess){
        return null
    }

    const { t } = useTranslation();
    const BANK_TABLE_FIELDS_CONFIG = useBankTableFieldsConfig();

    const [dataLocal, setDataLocal] = useState([]);
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const { data: banksData, isLoading } = useBanks(searchParams);

    useEffect(() => {
        if (banksData?.results) {
            setDataLocal(banksData.results);
            setRecordsCount(banksData.count);
        }
    }, [banksData, dataPage, dataPageSize]);

    const onClickButton = useCallback((e, id) => {
        e.stopPropagation();
    }, []);

    const banksDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            BANK_TABLE_FIELDS_CONFIG, // @ts-ignore
            hiddenColumnsForTables?.[BANK_TABLE_NAME] || [], // @ts-ignore
            columnsOrdersForTables?.[BANK_TABLE_NAME] || [],
        );
    }, [BANK_TABLE_FIELDS_CONFIG, hiddenColumnsForTables, columnsOrdersForTables]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                BANK_TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[BANK_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[BANK_TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[BANK_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[BANK_TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            BANK_TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    const tableComponent = () => (
        <FilterTable
            // @ts-ignore
            data={data}
            countRecords={recordsCount}
            resetPageNumber={resetPageNumber}
            style={{ maxHeight: "60vh" }}
            headersConfig={TableService.orderColumns(
                TableService.hideHiddenColumns(
                    BANK_TABLE_FIELDS_CONFIG, // @ts-ignore
                    hiddenColumnsForTables?.[BANK_TABLE_NAME] || [],
                ),
                // @ts-ignore
                columnsOrdersForTables?.[BANK_TABLE_NAME] || [],
            )}
            showCleanFilterIcon={true}
            showContextMenu={false}
            showExportToFileButton={true}
            exportToFileUrl={"bankings/banks/export/"}
            exportToFileSearchParams={searchParams}
            exportToFileHeaders={banksDataExportHeaders}
            exportToFileFileName={`${t("bankings.exportBanksFileName")}.xlsx`}
            showDetailsIcon={false}
            showCheckbox={false}
            page={dataPage}
            onPageChange={handleChangeDataPage}
            rowsPerPage={dataPageSize}
            onRowsPerPageChange={handleChangeDataPageSize}
            setDataLocal={setDataLocal}
            isLoading={isLoading}
        />
    );

      const { openDialog: openAddBankDialog } = useDialogBetter();
    
      const handleOpenAddBankDialog = (e) => {
        openAddBankDialog({
          Component: AddBankDialog,
        });
      };

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.bankings")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                rowSpacing={1}
            >
                <Grid item xs={12}>
                    < BankingNavigationBar />
                </Grid>
                <Grid item xs={2}>
                    <Button fullWidth variant="contained" onClick={handleOpenAddBankDialog}>
                        {t("bankings.add_bank")}
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={false}>{tableComponent()}</LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
